import {
  Box,
  Flex,
  Grid,
  HStack,
  VStack,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  IconButton,
  Badge,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Divider,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaKey, FaEye, FaEyeSlash, FaEdit, FaTrash, FaCheckCircle, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';
import { MdOutlineSmartToy } from 'react-icons/md';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanButton } from '../helivan/HelivanButton';
import { StyledSelect } from '../helivan/StyledSelect';
import {
  useGetMyCustomAgentsQuery,
  useUpdateCustomAgentMutation,
  useDeleteCustomAgentMutation,
  useGetAgentHealthSummaryQuery,
  CustomAgent,
} from '../../generated/graphql';

export const AgentSettings = () => {
  const toast = useToast();
  const [selectedAgent, setSelectedAgent] = useState<CustomAgent | null>(null);
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
  const apiKeyModal = useDisclosure();
  const deleteConfirmModal = useDisclosure();

  const { data: agentsData, loading: agentsLoading, refetch } = useGetMyCustomAgentsQuery({
    variables: { ownedOnly: true },
  });

  const { data: healthData } = useGetAgentHealthSummaryQuery();

  const [updateAgent, { loading: updateLoading }] = useUpdateCustomAgentMutation();
  const [deleteAgent, { loading: deleteLoading }] = useDeleteCustomAgentMutation();

  const agents = (agentsData?.getMyCustomAgents ?? []) as CustomAgent[];
  const healthSummary = healthData?.getAgentHealthSummary;

  const handleUpdateApiKey = async (agentId: string, apiKey: string, authType: string) => {
    if (!apiKey.trim()) {
      toast({
        title: 'API key is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      let apiCredentials: string;
      if (authType === 'API_KEY') {
        apiCredentials = JSON.stringify({ apiKey });
      } else if (authType === 'BEARER') {
        apiCredentials = JSON.stringify({ token: apiKey });
      } else {
        apiCredentials = JSON.stringify({ accessToken: apiKey });
      }

      await updateAgent({
        variables: {
          id: agentId,
          input: { apiCredentials },
        },
      });

      toast({
        title: 'API credentials updated',
        status: 'success',
        duration: 3000,
      });
      apiKeyModal.onClose();
      setSelectedAgent(null);
    } catch (error) {
      toast({
        title: 'Failed to update credentials',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    try {
      await deleteAgent({
        variables: { id: agentId },
      });

      toast({
        title: 'Agent deleted',
        status: 'success',
        duration: 3000,
      });
      deleteConfirmModal.onClose();
      setSelectedAgent(null);
      refetch();
    } catch (error) {
      toast({
        title: 'Failed to delete agent',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge colorScheme="green">Active</Badge>;
      case 'PENDING_VERIFICATION':
        return <Badge colorScheme="yellow">Pending</Badge>;
      case 'SUSPENDED':
        return <Badge colorScheme="red">Suspended</Badge>;
      default:
        return <Badge colorScheme="gray">Inactive</Badge>;
    }
  };

  const getAuthTypeLabel = (authType: string) => {
    switch (authType) {
      case 'API_KEY':
        return 'API Key';
      case 'BEARER':
        return 'Bearer Token';
      case 'OAUTH':
        return 'OAuth';
      default:
        return authType;
    }
  };

  if (agentsLoading) {
    return (
      <Flex justify="center" align="center" py={10}>
        <HelivanText variant="largeBody">Loading agents...</HelivanText>
      </Flex>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Health Summary */}
      {healthSummary && healthSummary.total > 0 && (
        <Box
          p={4}
          bg={HelivanColor.background.lightestGrey}
          borderRadius="8px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
        >
          <HelivanText variant="subheader" mb={3}>Agent Health Overview</HelivanText>
          <HStack spacing={6}>
            <HStack spacing={2}>
              <FaCheckCircle color="green" />
              <Text fontSize="14px">{healthSummary.healthy} Healthy</Text>
            </HStack>
            <HStack spacing={2}>
              <FaExclamationTriangle color="orange" />
              <Text fontSize="14px">{healthSummary.warning} Warning</Text>
            </HStack>
            <HStack spacing={2}>
              <FaTimesCircle color="red" />
              <Text fontSize="14px">{healthSummary.error} Error</Text>
            </HStack>
            <HStack spacing={2}>
              <MdOutlineSmartToy color={HelivanColor.text.blueMid} />
              <Text fontSize="14px">{healthSummary.total} Total</Text>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Agents List */}
      <Box>
        <HelivanText variant="subheader" mb={4}>My Custom Agents</HelivanText>

        {agents.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={10}
            bg={HelivanColor.background.lightestGrey}
            borderRadius="8px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
          >
            <MdOutlineSmartToy size={48} color={HelivanColor.text.blueMid} />
            <HelivanText variant="largeBody" colorOverride={HelivanColor.text.blueMid} mt={4}>
              No custom agents yet
            </HelivanText>
            <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid} mt={2}>
              Add agents from the Agents page to manage them here
            </HelivanText>
          </Flex>
        ) : (
          <VStack spacing={3} align="stretch">
            {agents.map((agent) => (
              <Box
                key={agent.id}
                p={4}
                bg="white"
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                _hover={{ borderColor: HelivanColor.text.helivanBlue }}
                transition="border-color 0.2s"
              >
                <Flex justify="space-between" align="flex-start">
                  <VStack align="flex-start" spacing={2} flex={1}>
                    <HStack spacing={3}>
                      <HelivanText variant="subheader">{agent.name}</HelivanText>
                      {getStatusBadge(agent.status)}
                    </HStack>

                    {agent.description && (
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        {agent.description}
                      </HelivanText>
                    )}

                    <HStack spacing={4} mt={2}>
                      <HStack spacing={1}>
                        <FaKey size={12} color={HelivanColor.text.blueMid} />
                        <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                          {getAuthTypeLabel(agent.apiAuthType)}
                        </Text>
                      </HStack>
                      <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                        Endpoint: {agent.apiEndpoint.length > 40
                          ? agent.apiEndpoint.substring(0, 40) + '...'
                          : agent.apiEndpoint}
                      </Text>
                    </HStack>
                  </VStack>

                  <HStack spacing={2}>
                    <Tooltip label="Update API Credentials">
                      <IconButton
                        aria-label="Update API key"
                        icon={<FaKey />}
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => {
                          setSelectedAgent(agent);
                          apiKeyModal.onOpen();
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Delete Agent">
                      <IconButton
                        aria-label="Delete agent"
                        icon={<FaTrash />}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={() => {
                          setSelectedAgent(agent);
                          deleteConfirmModal.onOpen();
                        }}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      {/* Update API Key Modal */}
      <UpdateApiKeyModal
        isOpen={apiKeyModal.isOpen}
        onClose={() => {
          apiKeyModal.onClose();
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
        onUpdate={handleUpdateApiKey}
        loading={updateLoading}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModal.isOpen} onClose={deleteConfirmModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Agent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete <strong>{selectedAgent?.name}</strong>?
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={deleteConfirmModal.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={() => selectedAgent && handleDeleteAgent(selectedAgent.id)}
              isLoading={deleteLoading}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

interface UpdateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: CustomAgent | null;
  onUpdate: (agentId: string, apiKey: string, authType: string) => void;
  loading: boolean;
}

const UpdateApiKeyModal = ({ isOpen, onClose, agent, onUpdate, loading }: UpdateApiKeyModalProps) => {
  const [apiKey, setApiKey] = useState('');
  const [authType, setAuthType] = useState('API_KEY');
  const [showKey, setShowKey] = useState(false);

  const handleSubmit = () => {
    if (agent) {
      onUpdate(agent.id, apiKey, authType);
      setApiKey('');
    }
  };

  const getKeyLabel = () => {
    switch (authType) {
      case 'API_KEY':
        return 'API Key';
      case 'BEARER':
        return 'Bearer Token';
      case 'OAUTH':
        return 'Access Token';
      default:
        return 'Credentials';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update API Credentials</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Box w="full">
              <Text fontWeight={500} mb={2}>Agent: {agent?.name}</Text>
              <Text fontSize="sm" color="gray.500">
                Endpoint: {agent?.apiEndpoint}
              </Text>
            </Box>

            <Divider />

            <FormControl>
              <StyledSelect
                label="Authentication Type"
                options={[
                  { value: 'API_KEY', label: 'API Key' },
                  { value: 'BEARER', label: 'Bearer Token' },
                  { value: 'OAUTH', label: 'OAuth' },
                ]}
                value={authType}
                onChange={(value) => setAuthType(value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{getKeyLabel()}</FormLabel>
              <InputGroup>
                <Input
                  type={showKey ? 'text' : 'password'}
                  placeholder={`Enter new ${getKeyLabel().toLowerCase()}`}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showKey ? 'Hide' : 'Show'}
                    icon={showKey ? <FaEyeSlash /> : <FaEye />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowKey(!showKey)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1}>
                This will replace the existing credentials
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={!apiKey.trim()}
          >
            Update Credentials
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
